import React, { useContext } from 'react';
import { useObjectRef } from 'react-aria';
import { RadioButton, RadioField } from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { FieldMessage, useFieldMessageIds } from '../field/field-shell';
import { RadioGroupStyleContext } from './group-context';

/**
 * ComplyKit Radio: React Aria Components (RadioField + RadioButton)
 * + Tailwind v4 + tailwind-variants.
 * Props are fully abstracted from the underlying library.
 *
 * A Radio only exists inside a RadioGroup: the group owns the selection, the
 * name and the validation state, and each Radio identifies itself through
 * `value`. There are therefore no isSelected/onChange props here, and no
 * per-item isInvalid: one invalid choice is a contradiction in a
 * single-select control, so validity belongs to the group.
 *
 * The label text renders INSIDE the RadioButton (the pressed <label>
 * wrapping the input), so clicking the circle or the text selects and the
 * text is the accessible name. See the checkbox for the note about
 * automation-dispatched (virtual) pointer events.
 * The unselected circle is transparent so the control composes on any
 * surface, matching Checkbox.
 */
interface RadioCommonProps {
  /** The value this radio contributes when selected (required by the group) */
  value: string;
  /** Help under the label: what this choice means. Inline content only. */
  description?: React.ReactNode;
  /** Whether this radio alone is disabled (the group can disable all) */
  isDisabled?: boolean;
  /** The id of the radio input */
  id?: string;
  /**
   * Visual emphasis of the selected state (the unselected circle is
   * identical across variants). From the canonical ComplyKit variant
   * vocabulary. Defaults to the enclosing RadioGroup's variant.
   */
  variant?: 'solid' | 'soft' | 'outline';
  /**
   * Recolours the control onto a semantic scale: `critical` for a choice
   * that destroys, `ai` for one the agent made. Absent means the brand
   * accent.
   */
  tone?: 'critical' | 'ai';
  /** Size of the radio. Defaults to the enclosing RadioGroup's size. */
  size?: Size;
}

/* A radio must have a name, and the type system holds the line the same way
 * Button's does: visible children are the accessible name, and the rare
 * label-less circle carries a required aria-label instead. An unnamed radio
 * does not compile instead of shipping silent to a screen reader. */
interface RadioLabelledProps extends RadioCommonProps {
  /** The visible label; renders inside the pressed label element, so it is
   * also the accessible name. */
  children: React.ReactNode;
  /** Override for the rare radio whose visible label needs rephrasing for
   * screen readers. */
  'aria-label'?: string;
}

interface RadioUnlabelledProps extends RadioCommonProps {
  children?: never;
  /** Accessible name, required when there is no visible label. */
  'aria-label': string;
}

export type RadioProps = RadioLabelledProps | RadioUnlabelledProps;

export const radioStyles = tv({
  slots: {
    root: ['ck:group ck:font-sans ck:text-text-default', 'ck:select-none'],
    // The button is a group too: react-aria places data-focus-visible (and
    // press states) on the pressed label, not on the field root. It washes
    // out alone when disabled, so the description stays legible.
    button: [
      'ck:group ck:flex ck:items-center ck:gap-2',
      'ck:group-disabled:cursor-not-allowed ck:group-disabled:opacity-50',
    ],
    // Inset past the circle and its gap, so the message starts under the label.
    message: 'ck:mt-1',
    // rounded-6 rather than a "full" radius, the same circle the switch
    // thumb draws: at these sizes 16px of radius is already circular.
    // No transition: the dot appears instantly, and a fill that fades under
    // an instant dot reads as flicker, so selection snaps like Chakra's did.
    box: [
      'ck:flex ck:shrink-0 ck:items-center ck:justify-center',
      'ck:rounded-6 ck:border ck:border-border-default ck:bg-transparent',
      'ck:group-focus-visible:focus-ring',
      // Stacked, as Checkbox's, so the chosen circle of an invalid group stays red.
      'ck:group-invalid:border-error-border ck:group-invalid:group-selected:border-error-border',
    ],
    // The dot is CSS-drawn from currentColor, like the checkbox's
    // indeterminate bar: the variant colours the box's text and the dot
    // follows with no second decision.
    dot: 'ck:rounded-6 ck:bg-current',
  },
  variants: {
    /* The circles are the Chakra ramp the product rendered (12/16/20); the
     * labels sit on the field ramp (12/14/16, typography.md), as Checkbox's. */
    size: {
      sm: { root: 'ck:text-xs', box: 'ck:size-3', dot: 'ck:size-1', message: 'ck:choice-inset-sm' },
      md: {
        root: 'ck:text-sm',
        box: 'ck:size-4',
        dot: 'ck:size-1.5',
        message: 'ck:choice-inset-md',
      },
      lg: {
        root: 'ck:text-base',
        box: 'ck:size-5',
        dot: 'ck:size-2',
        message: 'ck:choice-inset-lg',
      },
    },
    /* Read-only as Checkbox's: the mark stays, the press cues go, the tone
     * turns neutral. */
    readOnly: {
      false: { button: 'ck:cursor-pointer', box: 'ck:group-hover:border-border-active' },
      true: { button: 'ck:cursor-default' },
    },
    variant: {
      solid: {
        box: [
          'ck:text-tone-contrast',
          'ck:group-selected:border-transparent ck:group-selected:bg-fill',
        ],
      },
      soft: {
        box: [
          'ck:text-tone-text',
          'ck:group-selected:border-tone-a-7 ck:group-selected:bg-tone-a-3',
        ],
      },
      outline: {
        box: ['ck:text-tone-text', 'ck:group-selected:border-tone-a-8'],
      },
    },
  },
});

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { children, description, size, variant, tone, ...props },
  ref,
) {
  const inputRef = useObjectRef(ref);
  const group = useContext(RadioGroupStyleContext);
  const readOnly = !!group.isReadOnly;
  const { root, button, box, dot, message } = radioStyles({
    size: size ?? group.size ?? 'md',
    variant: variant ?? group.variant ?? 'solid',
    readOnly,
  });
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, undefined);

  return (
    <RadioField
      {...props}
      inputRef={inputRef}
      aria-describedby={describedBy}
      className={root()}
      data-component="radio"
      data-tone={readOnly ? 'neutral' : (tone ?? 'accent')}
    >
      <RadioButton className={button()}>
        {({ isSelected }) => (
          <>
            <span className={box()} aria-hidden="true">
              {isSelected ? <span className={dot()} /> : null}
            </span>
            {children}
          </>
        )}
      </RadioButton>
      {description ? (
        <div className={message()}>
          <FieldMessage description={description} descriptionId={descriptionId} errorId={errorId} />
        </div>
      ) : null}
    </RadioField>
  );
});
